/**
 * 交易守衛工具 - 防止重複處理交易
 * 使用 sessionStorage 記錄交易處理時間，並自動清理過期記錄
 */

const STORAGE_KEY_PREFIX = 'tx_'
const EXPIRY_TIME = 30 * 60 * 1000 // 30 分鐘過期
const IS_DEVELOPMENT = process.env.NODE_ENV === 'development'

/**
 * 開發環境日誌輸出工具
 */
const logger = {
	log: (...args) => {
		if (IS_DEVELOPMENT) {
			console.log(...args)
		}
	},
	warn: (...args) => {
		if (IS_DEVELOPMENT) {
			console.warn(...args)
		}
	},
	error: (...args) => {
		// 錯誤訊息在所有環境都輸出
		console.error(...args)
	},
}

/**
 * 檢查 sessionStorage 是否可用
 * @returns {boolean} true 表示可用
 */
function isStorageAvailable() {
	try {
		const testKey = '__storage_test__'
		sessionStorage.setItem(testKey, 'test')
		sessionStorage.removeItem(testKey)
		return true
	} catch (err) {
		logger.warn('[TransactionGuard] sessionStorage 不可用:', err)
		return false
	}
}

/**
 * 清理過期的交易記錄
 * @param {number} expiryTime - 過期時間（毫秒），預設 30 分鐘
 */
export function cleanupExpiredTransactions(expiryTime = EXPIRY_TIME) {
	if (!isStorageAvailable()) {
		return
	}

	try {
		const now = Date.now()
		const keysToRemove = []

		// 遍歷所有 sessionStorage 項目
		for (let i = 0; i < sessionStorage.length; i++) {
			const key = sessionStorage.key(i)

			// 只處理交易相關的 key
			if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
				try {
					const timestamp = parseInt(sessionStorage.getItem(key), 10)

					// 檢查是否過期或無效
					if (isNaN(timestamp) || now - timestamp > expiryTime) {
						keysToRemove.push(key)
					}
				} catch (err) {
					// 如果解析失敗，也將其標記為需要清理
					logger.warn(`[TransactionGuard] 無法解析交易記錄: ${key}`, err)
					keysToRemove.push(key)
				}
			}
		}

		// 批量刪除過期記錄
		keysToRemove.forEach(key => {
			sessionStorage.removeItem(key)
		})

		if (keysToRemove.length > 0) {
			logger.log(
				`[TransactionGuard] 已清理 ${keysToRemove.length} 筆過期交易記錄`,
			)
		}
	} catch (err) {
		logger.error('[TransactionGuard] 清理過期交易時發生錯誤:', err)
	}
}

/**
 * 檢查交易是否已處理過
 * @param {string} orderPaymentId - 交易 ID
 * @returns {boolean} true 表示已處理過
 */
export function isTransactionProcessed(orderPaymentId) {
	if (!orderPaymentId) {
		return false
	}

	if (!isStorageAvailable()) {
		return false
	}

	const key = STORAGE_KEY_PREFIX + orderPaymentId
	const timestamp = sessionStorage.getItem(key)

	if (!timestamp) {
		return false
	}

	// 只檢查該筆交易是否過期
	const now = Date.now()
	const time = parseInt(timestamp, 10)

	if (isNaN(time) || now - time > EXPIRY_TIME) {
		sessionStorage.removeItem(key)
		return false
	}

	return true
}

/**
 * 標記交易為已處理
 * @param {string} orderPaymentId - 交易 ID
 */
export function markTransactionProcessed(orderPaymentId) {
	if (!orderPaymentId) {
		logger.warn('[TransactionGuard] 交易 ID 不能為空')
		return
	}

	if (!isStorageAvailable()) {
		logger.warn('[TransactionGuard] sessionStorage 不可用，無法標記交易')
		return
	}

	const key = STORAGE_KEY_PREFIX + orderPaymentId
	const timestamp = Date.now()

	try {
		sessionStorage.setItem(key, timestamp.toString())
		logger.log(`[TransactionGuard] 交易 ${orderPaymentId} 已標記為處理`)
	} catch (err) {
		logger.error('[TransactionGuard] 標記交易時發生錯誤:', err)
	}
}

/**
 * 清除特定交易記錄
 * @param {string} orderPaymentId - 交易 ID
 */
export function clearTransaction(orderPaymentId) {
	if (!orderPaymentId) {
		return
	}

	if (!isStorageAvailable()) {
		return
	}

	const key = STORAGE_KEY_PREFIX + orderPaymentId
	try {
		sessionStorage.removeItem(key)
		logger.log(`[TransactionGuard] 已清除交易 ${orderPaymentId} 的記錄`)
	} catch (err) {
		logger.error('[TransactionGuard] 清除交易記錄時發生錯誤:', err)
	}
}

/**
 * 清除所有交易記錄（慎用）
 */
export function clearAllTransactions() {
	if (!isStorageAvailable()) {
		return
	}

	try {
		const keysToRemove = []

		for (let i = 0; i < sessionStorage.length; i++) {
			const key = sessionStorage.key(i)
			if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
				keysToRemove.push(key)
			}
		}

		keysToRemove.forEach(key => {
			sessionStorage.removeItem(key)
		})

		logger.log(
			`[TransactionGuard] 已清除所有交易記錄 (${keysToRemove.length} 筆)`,
		)
	} catch (err) {
		logger.error('[TransactionGuard] 清除所有交易記錄時發生錯誤:', err)
	}
}

/**
 * 獲取交易處理時間
 * @param {string} orderPaymentId - 交易 ID
 * @returns {number|null} 時間戳或 null
 */
export function getTransactionTimestamp(orderPaymentId) {
	if (!orderPaymentId) {
		return null
	}

	if (!isStorageAvailable()) {
		return null
	}

	const key = STORAGE_KEY_PREFIX + orderPaymentId
	const timestamp = sessionStorage.getItem(key)

	return timestamp ? parseInt(timestamp, 10) : null
}

export default {
	cleanupExpiredTransactions,
	isTransactionProcessed,
	markTransactionProcessed,
	clearTransaction,
	clearAllTransactions,
	getTransactionTimestamp,
}
